import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatDate } from '../../lib/utils';
import { 
  ClipboardList, CheckCircle, XCircle, RefreshCw, ExternalLink 
} from 'lucide-react';

export default function AdminApplications({ 
  pendingApps, 
  onApprove, 
  onDeny, 
  onRefresh 
}) {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Pending Applications
        </h2>
        <Button variant="outline" onClick={onRefresh} className="rounded-none">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {pendingApps.length === 0 ? (
        <div className="text-center py-12 border border-zinc-200">
          <ClipboardList className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <p className="text-zinc-500">No pending applications</p>
        </div>
      ) : (
        <div className="space-y-6">
          {pendingApps.map(app => {
            const isTier1 = app.application?.application_tier === 'declaration';
            const appStatus = app.application?.status;
            
            return (
              <div key={app.organization.id} className="border border-zinc-200 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold">{app.organization.display_name}</h3>
                    <p className="text-sm text-zinc-500">{app.organization.legal_name}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge 
                        variant="outline" 
                        className={`rounded-none ${isTier1 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}
                      >
                        {isTier1 ? 'Tier 1: Full Accountability Declaration' : 'Tier 2: Accountability Declared'}
                      </Badge>
                      {appStatus && (
                        <Badge variant="outline" className="rounded-none text-xs">
                          Status: {appStatus.replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => onApprove(app.organization.id)}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`approve-${app.organization.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => onDeny(app)}
                      data-testid={`deny-${app.organization.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Deny
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-6 mb-4">
                  <div>
                    <p className="text-sm text-zinc-500">Website</p>
                    <a href={app.organization.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {app.organization.website}
                    </a>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Contact Email</p>
                    <p>{app.organization.contact_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Applied</p>
                    <p>{formatDate(app.application?.created_at)}</p>
                  </div>
                </div>

                {/* Tier 1 Requirements Checklist */}
                {isTier1 && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded">
                    <p className="text-sm font-medium text-amber-800 mb-2">Tier 1 Accountability Declaration Requirements</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className={app.application?.scope_statement ? 'text-emerald-700' : 'text-red-600'}>
                        {app.application?.scope_statement ? '✓' : '✗'} Scope Statement
                      </div>
                      <div className={app.application?.responsibility_domain ? 'text-emerald-700' : 'text-red-600'}>
                        {app.application?.responsibility_domain ? '✓' : '✗'} Responsibility Domain (AI-ROI-1.0)
                      </div>
                      <div className={app.application?.escalation_structure ? 'text-emerald-700' : 'text-red-600'}>
                        {app.application?.escalation_structure ? '✓' : '✗'} Escalation Structure (AI-HITL-1.0)
                      </div>
                      <div className={app.application?.incident_response_doc ? 'text-emerald-700' : 'text-red-600'}>
                        {app.application?.incident_response_doc ? '✓' : '✗'} Incident Response (AI-READ-1.0)
                      </div>
                      <div className={app.application?.change_control_doc ? 'text-emerald-700' : 'text-red-600'}>
                        {app.application?.change_control_doc ? '✓' : '✗'} Change Control (AI-PERF-1.0)
                      </div>
                      <div className={app.application?.client_disclosure_ack ? 'text-emerald-700' : 'text-red-600'}>
                        {app.application?.client_disclosure_ack ? '✓' : '✗'} Client Disclosure (AI-DISC-1.0)
                      </div>
                    </div>
                    <p className="text-sm font-medium text-amber-800 mt-3 mb-2">Accountability Boundaries (AI-ABND-1.0)</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className={app.application?.accountability_scope_doc_url ? 'text-emerald-700' : 'text-red-600'}>
                        {app.application?.accountability_scope_doc_url ? '✓' : '✗'} Scope Doc URL
                      </div>
                      <div className={app.application?.accountability_domains_doc_url ? 'text-emerald-700' : 'text-red-600'}>
                        {app.application?.accountability_domains_doc_url ? '✓' : '✗'} Domains Doc URL
                      </div>
                      <div className={app.application?.accountability_client_ack_url ? 'text-emerald-700' : 'text-red-600'}>
                        {app.application?.accountability_client_ack_url ? '✓' : '✗'} Client Ack URL
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-zinc-500 mb-2">Standards Applied For</p>
                    <div className="space-y-1">
                      {app.standards_applied?.map(stdId => (
                        <Badge key={stdId} variant="outline" className="mr-2 rounded-none">
                          {stdId}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500 mb-2">Payment</p>
                    {app.payment ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="rounded-none bg-green-50 text-green-700 border-green-200">
                          ${app.payment.payment_amount} Paid
                        </Badge>
                        <span className="text-xs text-zinc-500">{formatDate(app.payment.payment_date)}</span>
                      </div>
                    ) : (
                      <Badge variant="outline" className="rounded-none">No payment</Badge>
                    )}
                  </div>
                </div>

                {app.reports && app.reports.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-zinc-200">
                    <p className="text-sm text-zinc-500 mb-2">Assessment Reports ({app.reports.length})</p>
                    <div className="space-y-2">
                      {app.reports.map(report => (
                        <div key={report.id} className="flex items-center justify-between bg-zinc-50 p-2">
                          <div>
                            <span className="font-mono text-sm">{report.report_id}</span>
                            <span className="text-sm text-zinc-500 ml-2">{report.tool_name}</span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => window.open(`/reports/${report.report_id}`, '_blank')}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
